import { httpClient } from "@coworker/http-client";
import {
  Criterion,
  TestBuyProductWithCriteria,
} from "@coworker/types/lib/testbuy";

type InsufficientHfb = {
  hfb: string;
  amountOfAvailableArticles: number;
};

export type CreateTestbuyParams = {
  taskId: string;
  storeId: string;
  countryId: string;
  locale: string;
};

type CreateTestbuyResponse = {
  testBuyId?: string;
  message?: string;
  insufficientHfbs?: InsufficientHfb[];
};

export type Testbuy = {
  id: string;
  status: string;
  numberOfArticles: number;
  storeId: string;
  createdAt: string;
  createdAtTs?: number;
  finishedAtTs?: number;
  includeOutgoingArticles: boolean;
  includeOutOfStockArticles: boolean;
  createdBy: string;
  customName?: string;
  strategy: string;
  areas: string[];
  tasks: unknown[];
  articles: TestBuyProductWithCriteria[];
  // The Task Management V2 task that this testbuy is linked to (the task the testbuy
  // was started from). Used to navigate back to its task viewer.
  taskId?: string;
};

export type ReplaceTestbuyArticleParams =
  | {
      locale: string;
      countryId: string;
      area?: string;
      excludedArticleShortId?: string[];
    }
  | {
      locale: string;
      countryId: string;
      articleShortId: string;
    };

/**
 * Creates a testbuy linked to a Task Management V2 task (`taskId`). The task must not already
 * be linked to another testbuy.
 */
export async function createTestbuy(params: CreateTestbuyParams) {
  return httpClient.testbuy.post<CreateTestbuyResponse>("/v2/testbuy", {
    body: params,
  });
}

/**
 * Fetches a testbuy by id, scoped to task-linked testbuys, including its articles and their
 * criteria.
 */
export async function getTestbuy(testbuyId: string) {
  return httpClient.testbuy.get<Testbuy>(`/v2/testbuy/${testbuyId}`);
}

/**
 * Replaces an article in a testbuy. Pass `articleShortId` to replace with a specific article,
 * or omit it to get a random article from `area` instead.
 */
export async function replaceTestbuyArticle(
  testbuyId: string,
  articleId: string,
  params: ReplaceTestbuyArticleParams
) {
  return httpClient.testbuy.post<TestBuyProductWithCriteria>(
    `/v2/testbuy/${testbuyId}/articles/${articleId}/replace`,
    { body: params }
  );
}

/**
 * Updates a single criterion of a testbuy article. Also keeps the article's parent task
 * progress/completion in sync.
 */
export async function updateTestbuyArticleCriterion(
  testbuyId: string,
  articleId: string,
  criterion: Criterion
) {
  return httpClient.testbuy.patch<{ id: string }[]>(
    `/v2/testbuy/${testbuyId}/articles/${articleId}/criterion`,
    { body: { criterion } }
  );
}
